import { useState, useMemo } from "react";
import { useFetch } from "@/lib/useFetch";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PageHeader } from "@/mockups/ui";
import MonthGrid from "@/components/rrhh/MonthGrid";
import { cn } from "@/lib/utils";
import { Filter, Check, Star } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const initials = (name) => (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("");

function chipClass(e) {
  if (e.type === "festivo") return "bg-gold/15 text-[#a9741c] dark:text-gold border-transparent";
  if (e.status === "pending") return "border-dashed border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300";
  if (e.type === "ausencia") return "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/15 dark:text-orange-300";
  return "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/15 dark:text-sky-300";
}

export default function CalendarioEquipo({ embedded = false }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const month = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;
  const { data: team } = useFetch("/team");
  const users = useMemo(() => team || [], [team]);
  const [selected, setSelected] = useState(null); // null = todos

  const params = useMemo(() => (selected ? { month, user_ids: selected.join(",") } : { month }), [month, selected]);
  const { data, loading } = useFetch("/hr/calendar/month", params);

  const toggle = (id) => setSelected((s) => {
    const base = s || users.map((u) => u.user_id);
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    return next.length === users.length ? null : next;
  });
  const selCount = selected ? selected.length : users.length;

  const renderEvent = (e, i) => {
    if (e.type === "festivo") {
      return (
        <div key={i} className={cn("flex items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[11px]", chipClass(e))} title={e.label} data-testid="team-event-festivo">
          <Star className="h-3 w-3 shrink-0" /><span className="truncate">{e.label}</span>
        </div>
      );
    }
    return (
      <div key={i} className={cn("flex items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[11px]", chipClass(e))} title={`${e.name} · ${e.req_type}${e.status === "pending" ? " (pendiente)" : ""}`} data-testid={`team-event-${e.type}`}>
        <Avatar className="h-4 w-4 shrink-0"><AvatarImage src={e.picture} /><AvatarFallback className="text-[8px] bg-primary/15 text-primary">{initials(e.name)}</AvatarFallback></Avatar>
        <span className="truncate">{e.name.split(" ")[0]}</span>
      </div>
    );
  };

  const filterBtn = (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" data-testid="team-cal-filter"><Filter className="h-4 w-4" /> {selected ? `${selCount} personas` : "Todos"}</Button>
      </PopoverTrigger>
      <PopoverContent className="max-h-80 w-64 overflow-y-auto p-2" align="end">
        <button onClick={() => setSelected(null)} className="mb-1 flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted" data-testid="team-cal-filter-all">
          <span className="font-medium">Ver a todos</span>{!selected && <Check className="h-4 w-4 text-primary" />}
        </button>
        <div className="border-t border-border pt-1">
          {users.map((u) => {
            const on = !selected || selected.includes(u.user_id);
            return (
              <button key={u.user_id} onClick={() => toggle(u.user_id)} className="flex w-full items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-muted" data-testid={`team-cal-filter-${u.user_id}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <Avatar className="h-6 w-6"><AvatarImage src={u.picture} /><AvatarFallback className="text-[9px] bg-primary/15 text-primary">{initials(u.name)}</AvatarFallback></Avatar>
                  <span className="truncate">{u.name}</span>
                </span>
                {on && <Check className="h-4 w-4 text-primary shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="calendario-equipo-page">
      {!embedded && <PageHeader title="Calendario de equipo" subtitle="Quién está de vacaciones o ausente y los festivos, para coordinaros sin solapamientos" />}

      <MonthGrid data={data} loading={loading} cursor={cursor} setCursor={setCursor} renderEvent={renderEvent} headerRight={filterBtn} maxPerDay={4} />

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-sky-400" /> Vacaciones</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-400" /> Ausencia</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded border border-dashed border-amber-400 bg-amber-50" /> Solicitado (pendiente)</span>
        <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-gold" /> Festivo</span>
      </div>
    </div>
  );
}
